import qs from 'qs';

import { definitions, paths } from '@luxuryescapes/contract-svc-lux-loyalty';

import type {
  CppExpiringResponse,
  CppPartner,
  CppRateCard,
  CppRateCardListFilters,
  CreateCppRateCardBody,
  UpdateCppRateCardBody,
} from '~/types/costPerPoint';
import type {
  AdminSubmitClaimRequestBody,
  AdminSubmitClaimResponse,
  ApproveApprovalRequestBody,
  ApproveApprovalRequestResponse,
  GetApprovalRequestResponse,
  ListApprovalRequestsParams,
  ListApprovalRequestsResponse,
  ListCampaignsResponse,
  LoyaltyAccountSummary,
  PointsAdjustmentRequestBody,
  PointsAdjustmentResponse,
  PromoCampaignBody,
  PromoCampaignCreateResponse,
  PromoCampaignDeleteResponse,
  PromoCampaignReadResponse,
  PromoCampaignSearchParams,
  PromoCampaignSearchResponse,
  PromoCampaignUpdateBody,
  PromoCampaignUpdateResponse,
  RejectApprovalRequestBody,
  RejectApprovalRequestResponse,
  TierChangeRequestBody,
  TierChangeResponse,
} from '~/types/loyaltyContract';

import { EmptyArray, arrayToObject } from '~/utils/arrayUtils';
import { getMembershipYearEndDate, isLuxLoyaltyEnabled } from '~/utils/luxLoyalty';

import { request } from './common';

type RequestOptions = {
  signal?: AbortSignal;
};

type GetLuxLoyaltyAccountResponse = LoyaltyAccountSummary;

type GetLuxLoyaltyProgramConfigResponse =
  paths['/api/lux-loyalty/program-configuration']['get']['responses']['200']['content']['application/json']['result'];

type GetLuxLoyaltyActivityLogResponse =
  paths['/api/lux-loyalty/account/activities']['get']['responses']['200']['content']['application/json'];

type GetLuxLoyaltyPendingPointsResponse =
  paths['/api/lux-loyalty/customer/{customerId}/order/{orderId}/pending-points']['get']['responses']['200']['content']['application/json'];

type AdminLuxLoyaltyGetAccrualsByOrderResponse =
  paths['/api/lux-loyalty/admin/earn/order/{orderId}']['get']['responses']['200']['content']['application/json'];

type AccountUpdateRegionRequest =
  paths['/api/lux-loyalty/account/region']['patch']['requestBody']['content']['application/json'];

type AccountUpdateRegionResponse =
  paths['/api/lux-loyalty/account/region']['patch']['responses']['200']['content']['application/json'];

type LuxLoyaltyAccountRegions = definitions['accountRegions'];

export type LuxLoyaltyAccrual = AdminLuxLoyaltyGetAccrualsByOrderResponse['result']['accruals'];

type AccountInternalCreateResponse =
  paths['/api/lux-loyalty/account/internal']['post']['responses']['201']['content']['application/json'];

export type AccountInternalCreateRequest =
  paths['/api/lux-loyalty/account/internal']['post']['requestBody']['content']['application/json'];

export type InfiniteOnboardingEmailResponse =
  paths['/api/lux-loyalty/account/{customerId}/infinite-onboarding-email']['post']['responses']['200']['content']['application/json'];

type InfiniteOnboardingEmailWarningResponse =
  | AccountInternalCreateResponse
  | TierChangeResponse
  | InfiniteOnboardingEmailResponse;

type InfiniteOnboardingEmailStatus = NonNullable<
  NonNullable<InfiniteOnboardingEmailWarningResponse['result']['sideEffects']>['infiniteOnboardingEmail']
>;

type InfiniteOnboardingEmailReason = Extract<InfiniteOnboardingEmailStatus, { reason: string }>['reason'];

const infiniteOnboardingEmailFailureMessages: Record<NonNullable<InfiniteOnboardingEmailReason>, string> = {
  account_not_found: 'Loyalty account was not found.',
  customer_service_rep_not_found: 'Assigned concierge could not be found.',
  email_verification_error: 'Customer email verification failed before sending.',
  missing_customer_service_rep: 'Customer has no assigned concierge.',
  notification_proxy_error: 'Notification service failed to send the email.',
};

export function getInfiniteOnboardingEmailWarning(response: InfiniteOnboardingEmailWarningResponse): string | null {
  const emailStatus = response.result?.sideEffects?.infiniteOnboardingEmail;

  if (!emailStatus || emailStatus.status === 'sent') {
    return null;
  }

  const reasonMessage = emailStatus.reason ? infiniteOnboardingEmailFailureMessages[emailStatus.reason] : null;
  return `Infinite onboarding email was not sent${reasonMessage ? `: ${reasonMessage}` : '.'}`;
}

const BASE_PATH = `${window.configs.API_HOST}/api/lux-loyalty`;

function mapAccountSummaryToLuxLoyaltyAccount(
  accountSummary: GetLuxLoyaltyAccountResponse,
): App.LuxLoyaltyAccount | null {
  if (!accountSummary) {
    return null;
  }

  return {
    customerId: accountSummary.customerId,
    points: accountSummary.points,
    tier: accountSummary.tier,
    statusCredits: accountSummary.statusCredits,
    ibsMemberId: accountSummary.ibsMemberId,
    accountNumber: accountSummary.accountNumber ?? undefined,
    createdAt: accountSummary.createdAt ?? undefined,
    statusReviewDate: accountSummary.statusReviewDate ?? undefined,
    membershipYearEndDate:
      (accountSummary as GetLuxLoyaltyAccountResponse & { membershipYearEndDate?: string }).membershipYearEndDate ??
      (accountSummary.createdAt ? getMembershipYearEndDate(accountSummary.createdAt) : undefined),
    totalCreditsForTierRetention: accountSummary.totalCreditsForTierRetention ?? undefined,
    totalCreditsForTierUpgrade: accountSummary.totalCreditsForTierUpgrade ?? undefined,
    numberOfNights: accountSummary.numberOfNights,
    numberOfNightsForTierUpgrade: accountSummary.numberOfNightsForTierUpgrade ?? undefined,
    numberOfNightsForTierRetention: accountSummary.numberOfNightsForTierRetention ?? undefined,
    benefits: accountSummary.benefits.map(
      (benefit): App.LuxLoyaltyBenefit => ({
        code: benefit.code,
        description: benefit.description,
        details: {
          // convert 'unlimited' to infinity as it's a lot easier to work with numbers, not number | string
          remaining: benefit.details.remaining === 'unlimited' ? Infinity : benefit.details.remaining,
          limit: benefit.details.limit === 'unlimited' ? Infinity : benefit.details.limit,
          value: benefit.details.value
            ? {
                amount: benefit.details.value.amount,
                unit: benefit.details.value.unit,
                currency: benefit.details.value.currency,
              }
            : undefined,
        },
      }),
    ),
    region: accountSummary.region,
    customerServiceRep: accountSummary.customerServiceRep ?? null,
    infiniteProfile: accountSummary.infiniteProfile ?? null,
  };
}

async function getLuxLoyaltyAccount(customerId: string): Promise<App.LuxLoyaltyAccount | null> {
  if (!customerId || !isLuxLoyaltyEnabled()) {
    return null;
  }

  const queryParams = qs.stringify({ customerId });
  const uri = `${BASE_PATH}/account?${queryParams}`;

  const { result } = await request(uri, { method: 'GET' });
  const accountSummary: GetLuxLoyaltyAccountResponse = result.accountSummary;

  return mapAccountSummaryToLuxLoyaltyAccount(accountSummary);
}

/**
 * Fetch a Lux Loyalty account using the loyalty account number.
 */
async function getLuxLoyaltyAccountByNumber(accountNumber: number | string): Promise<App.LuxLoyaltyAccount | null> {
  if (!isLuxLoyaltyEnabled() || accountNumber == null) {
    return null;
  }

  const value = String(accountNumber).trim();
  const uri = `${BASE_PATH}/account/account-number/${value}`;

  const { result } = await request(uri, { method: 'GET' });
  const accountSummary: GetLuxLoyaltyAccountResponse = result.accountSummary;
  return mapAccountSummaryToLuxLoyaltyAccount(accountSummary);
}

function mapLuxLoyaltyProgramConfig(response: GetLuxLoyaltyProgramConfigResponse): App.LuxLoyaltyProgramConfig {
  return {
    programName: response.programName,
    currencyName: response.currencyName,
    statusCreditsName: response.statusCreditsName,
    tierLabel: response.tierLabel,
    tiers: arrayToObject(response.tiers, (tier) => tier.id),
  };
}

async function getLuxLoyaltyProgramConfig(region?: string): Promise<GetLuxLoyaltyProgramConfigResponse | null> {
  if (!isLuxLoyaltyEnabled()) {
    return null;
  }

  const query = qs.stringify({
    region,
  });

  const uri = `${BASE_PATH}/program-configuration?${query}`;

  const { result } = await request(uri, { method: 'GET' });
  return result;
}

async function getLuxLoyaltyOrderPendingPoints(
  customerId: string,
  orderId: string,
): Promise<Array<App.LuxLoyaltyPendingPoint>> {
  if (!isLuxLoyaltyEnabled() || !customerId || !orderId) {
    return EmptyArray;
  }

  const uri = `${BASE_PATH}/customer/${customerId}/order/${orderId}/pending-points`;
  const response: GetLuxLoyaltyPendingPointsResponse = await request(uri, {
    method: 'GET',
  });

  return response.result.pendingPoints.map((pendingPointData): App.LuxLoyaltyPendingPoint => {
    const basePoints = pendingPointData.points;
    const bonusPoints = pendingPointData.bonusPoints;

    return {
      id: pendingPointData.id,
      createdAt: pendingPointData.createdAt,
      itemId: pendingPointData.itemId,
      orderId: pendingPointData.orderId,
      basePoints,
      bonusPoints,
      points: basePoints + bonusPoints,
      statusCredits: pendingPointData.statusCredits,
      bonusStatusCredits: pendingPointData.bonusStatusCredits,
      pointsEarnDate: pendingPointData.pointsEarnDate,
      status: pendingPointData.status,
      numberOfNights: pendingPointData.numberOfNights ?? null,
    };
  });
}

async function getLuxLoyaltyActivityLog(
  customerId: string,
  fromDate: string,
  toDate: string,
): Promise<App.LuxLoyaltyActivityLog | null> {
  const queryParams = qs.stringify({ customerId, fromDate, toDate });
  const uri = `${BASE_PATH}/account/activities?${queryParams}`;

  const response: GetLuxLoyaltyActivityLogResponse = await request(uri, { method: 'GET' });
  const { result } = response;

  return result.activities?.map((activity) => ({
    id: activity.id,
    activityDate: activity.activityDate,
    activityType: activity.activityType,
    activityDescription: activity.activityDescription,
    statusCredits: activity.statusCredits,
    points: activity.basePoints,
    numberOfNights: activity.numNights,
    currencyAmount: activity.currencyAmount,
    currency: activity.currency,
    orderId: activity.orderId,
    itemId: activity.itemId,
  }));
}

async function getLuxLoyaltyCustomerPendingPoints(customerId: string): Promise<Array<App.LuxLoyaltyPendingPoint>> {
  const uri = `${BASE_PATH}/customer/${customerId}/pending-points`;
  const response: GetLuxLoyaltyPendingPointsResponse = await request(uri, { method: 'GET' });
  const { result } = response;

  return result.pendingPoints.map((pendingPointData): App.LuxLoyaltyPendingPoint => {
    const basePoints = pendingPointData.points;
    const bonusPoints = pendingPointData.bonusPoints;

    return {
      id: pendingPointData.id,
      createdAt: pendingPointData.createdAt,
      itemId: pendingPointData.itemId,
      orderId: pendingPointData.orderId,
      basePoints,
      bonusPoints,
      points: basePoints + bonusPoints,
      statusCredits: pendingPointData.statusCredits,
      bonusStatusCredits: pendingPointData.bonusStatusCredits,
      pointsEarnDate: pendingPointData.pointsEarnDate ?? undefined,
      status: pendingPointData.status,
      numberOfNights: pendingPointData.numberOfNights ?? null,
    };
  });
}

async function getLuxLoyaltySuccessfulAccrualOnOrder(orderId: string): Promise<Array<App.LuxLoyaltyAccrualItem>> {
  if (!isLuxLoyaltyEnabled() || !orderId) {
    return EmptyArray;
  }

  const uri = `${BASE_PATH}/admin/earn/order/${orderId}`;
  const response: AdminLuxLoyaltyGetAccrualsByOrderResponse = await request(uri, { method: 'GET' });
  return response.result.accruals.map((accrual) => ({
    itemId: accrual.itemId,
    points: accrual.points,
    statusCredits: accrual.statusCredits,
    dateEarned: accrual.dateEarned,
  }));
}

/**
 * Updates the region for a Lux Loyalty account
 * @param customerId - The customer ID whose region needs to be updated
 * @param region - The new region code (e.g., 'SG', 'NZ', 'AU')
 * @returns The updated account summary response or null if Lux Loyalty is not enabled
 * @throws {Error} When Lux Loyalty is not enabled or customer ID is missing
 */
async function updateLuxLoyaltyAccountRegion(
  customerId: string,
  region: AccountUpdateRegionRequest['region'],
): Promise<AccountUpdateRegionResponse | null> {
  // Validate that Lux Loyalty is enabled and customer ID is provided
  if (!isLuxLoyaltyEnabled() || !customerId) {
    throw new Error('Lux Loyalty is not enabled or customer ID is missing');
  }

  const uri = `${BASE_PATH}/account/region`;
  const payload: AccountUpdateRegionRequest = {
    customerId,
    region,
  };

  // Make PATCH request to update the account region
  const response: AccountUpdateRegionResponse = await request(uri, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  return response;
}

/** Result of a pending points reconstruction attempt from BigQuery */
export interface ReconstructPendingPointsResult {
  /** 'created' when a new pending point record was inserted, 'skipped' when one already existed */
  status: 'created' | 'skipped';
  itemId: string;
  orderId: string;
}

/**
 * Triggers a BigQuery-based reconstruction of the pending points record for a specific order item.
 * Calls POST /api/lux-loyalty/pending-points/bq-import on svc-lux-loyalty.
 */
export async function reconstructPendingPoints(
  orderId: string,
  itemId: string,
): Promise<ReconstructPendingPointsResult> {
  const response = await request(`${BASE_PATH}/pending-points/bq-import`, {
    method: 'POST',
    body: JSON.stringify({ orderId, itemId }),
  });
  return response.result as ReconstructPendingPointsResult;
}

export {
  BASE_PATH,
  getLuxLoyaltyAccount,
  getLuxLoyaltyAccountByNumber,
  getLuxLoyaltyProgramConfig,
  mapLuxLoyaltyProgramConfig,
  getLuxLoyaltyActivityLog,
  getLuxLoyaltyCustomerPendingPoints,
  getLuxLoyaltyOrderPendingPoints,
  getLuxLoyaltySuccessfulAccrualOnOrder,
  updateLuxLoyaltyAccountRegion,
};

export async function adjustPoints(params: PointsAdjustmentRequestBody): Promise<PointsAdjustmentResponse> {
  return request(`${BASE_PATH}/points/adjustments`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function changeTier(params: TierChangeRequestBody): Promise<TierChangeResponse> {
  return request(`${BASE_PATH}/account/tier-change`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function getAllClaimCampaigns(): Promise<ListCampaignsResponse> {
  return request(`${BASE_PATH}/claims/campaigns/all`, { method: 'GET' });
}

export async function enrolAccount(customerId: string): Promise<unknown> {
  return request(`${BASE_PATH}/account`, {
    method: 'POST',
    body: JSON.stringify({ customerId }),
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Admin-only enrolment. When `tier` is omitted, the loyalty service applies
 * its default tier-resolution logic (prehandlers, falling back to bronze).
 * When `customerServiceRepId` is provided, the loyalty service links the
 * account to that rep as part of the enrolment. Backed by
 * POST /api/lux-loyalty/account/internal — restricted to admin and service
 * roles on the server side via checkUserAdminPermissions.
 */
export async function enrolAccountInternal(
  params: AccountInternalCreateRequest,
): Promise<AccountInternalCreateResponse> {
  return request(`${BASE_PATH}/account/internal`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Admin-only. Set or overwrite the customer service rep linked to an existing
 * Société account. Backed by
 * POST /api/lux-loyalty/account/{customerId}/customer-service-rep.
 */
export async function setAccountCustomerServiceRep(params: {
  customerId: string;
  customerServiceRepId: string;
}): Promise<unknown> {
  return request(`${BASE_PATH}/account/${encodeURIComponent(params.customerId)}/customer-service-rep`, {
    method: 'POST',
    body: JSON.stringify({ customerServiceRepId: params.customerServiceRepId }),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function resendInfiniteOnboardingEmail(customerId: string): Promise<InfiniteOnboardingEmailResponse> {
  return request(`${BASE_PATH}/account/${encodeURIComponent(customerId)}/infinite-onboarding-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function adminClaim(
  campaignId: string,
  params: AdminSubmitClaimRequestBody,
): Promise<AdminSubmitClaimResponse> {
  return request(`${BASE_PATH}/claims/campaigns/${campaignId}/admin-claim`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function listApprovalRequests(
  campaignId: string,
  params?: ListApprovalRequestsParams,
  options: RequestOptions = {},
): Promise<ListApprovalRequestsResponse> {
  const query = params
    ? `?${new URLSearchParams(Object.entries(params).filter(([, v]) => v != null) as Array<[string, string]>)}`
    : '';
  return request(`${BASE_PATH}/claims/campaigns/${campaignId}/approval-requests${query}`, {
    ...options,
    method: 'GET',
  });
}

export async function getApprovalRequest(
  requestId: string,
  options: RequestOptions = {},
): Promise<GetApprovalRequestResponse> {
  return request(`${BASE_PATH}/claims/approval-requests/${requestId}`, {
    ...options,
    method: 'GET',
  });
}

export async function approveApprovalRequest(
  requestId: string,
  body: ApproveApprovalRequestBody,
): Promise<ApproveApprovalRequestResponse> {
  return request(`${BASE_PATH}/claims/approval-requests/${requestId}/approve`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function rejectApprovalRequest(
  requestId: string,
  body: RejectApprovalRequestBody,
): Promise<RejectApprovalRequestResponse> {
  return request(`${BASE_PATH}/claims/approval-requests/${requestId}/reject`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

export const luxLoyaltyRegions: Record<LuxLoyaltyAccountRegions, string> = {
  AU: 'Australia',
  CA: 'Canada',
  CN: 'China',
  FR: 'France',
  DE: 'Germany',
  HK: 'Hong Kong',
  IN: 'India',
  ID: 'Indonesia',
  IE: 'Ireland',
  IL: 'Israel',
  IT: 'Italy',
  JP: 'Japan',
  KR: 'South Korea',
  MO: 'Macao',
  MY: 'Malaysia',
  NL: 'Netherlands',
  NZ: 'New Zealand',
  PH: 'Philippines',
  QA: 'Qatar',
  SA: 'Saudi Arabia',
  SG: 'Singapore',
  ZA: 'South Africa',
  ES: 'Spain',
  TW: 'Taiwan',
  TH: 'Thailand',
  AE: 'United Arab Emirates',
  GB: 'United Kingdom',
  US: 'United States',
  VN: 'Vietnam',
} as const;

export async function searchPromoCampaigns(
  params: PromoCampaignSearchParams,
  options: RequestOptions = {},
): Promise<PromoCampaignSearchResponse> {
  return request(`${BASE_PATH}/promo-campaigns/search`, {
    ...options,
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function createPromoCampaign(params: {
  campaign: PromoCampaignBody;
  userIds?: Array<string>;
}): Promise<PromoCampaignCreateResponse> {
  return request(`${BASE_PATH}/promo-campaigns`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function readPromoCampaign(promoCampaignId: string): Promise<PromoCampaignReadResponse> {
  return request(`${BASE_PATH}/promo-campaigns/${promoCampaignId}`, {
    method: 'GET',
  });
}

export async function updatePromoCampaign(
  promoCampaignId: string,
  params: { campaign: PromoCampaignUpdateBody; addUserIds?: Array<string> },
): Promise<PromoCampaignUpdateResponse> {
  return request(`${BASE_PATH}/promo-campaigns/${promoCampaignId}`, {
    method: 'PATCH',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function deletePromoCampaign(promoCampaignId: string): Promise<PromoCampaignDeleteResponse> {
  return request(`${BASE_PATH}/promo-campaigns/${promoCampaignId}`, {
    method: 'DELETE',
  });
}

const CPP_BASE = `${BASE_PATH}/partners/v1/cost-per-point`;

/** Lists CPP rate cards with optional partner/status filters. */
export async function listCppRateCards(
  filters: CppRateCardListFilters = {},
  options: RequestOptions = {},
): Promise<{ result: Array<CppRateCard> }> {
  const query = qs.stringify(filters, { skipNulls: true });
  return request(`${CPP_BASE}/rate-cards${query ? `?${query}` : ''}`, { ...options, method: 'GET' });
}

/** Fetches a single CPP rate card by id. */
export async function getCppRateCard(id: string, options: RequestOptions = {}): Promise<{ result: CppRateCard }> {
  return request(`${CPP_BASE}/rate-cards/${id}`, { ...options, method: 'GET' });
}

/** Creates a CPP rate card (as DRAFT). */
export async function createCppRateCard(body: CreateCppRateCardBody): Promise<{ result: CppRateCard }> {
  return request(`${CPP_BASE}/rate-cards`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

/** Updates a CPP rate card (state-aware on the server). */
export async function updateCppRateCard(id: string, body: UpdateCppRateCardBody): Promise<{ result: CppRateCard }> {
  return request(`${CPP_BASE}/rate-cards/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

/** Activates a DRAFT rate card. */
export async function activateCppRateCard(id: string): Promise<{ result: CppRateCard }> {
  return request(`${CPP_BASE}/rate-cards/${id}/activate`, { method: 'POST' });
}

/** Deactivates an ACTIVE rate card (started -> INACTIVE, future -> DRAFT). */
export async function deactivateCppRateCard(id: string): Promise<{ result: CppRateCard }> {
  return request(`${CPP_BASE}/rate-cards/${id}/deactivate`, { method: 'POST' });
}

/** Deletes a DRAFT rate card. */
export async function deleteCppRateCard(id: string): Promise<{ result: { id: string } }> {
  return request(`${CPP_BASE}/rate-cards/${id}`, { method: 'DELETE' });
}

/** Lists ACTIVE rate cards expiring within `withinDays` (default 30). */
export async function listExpiringCppRateCards(
  withinDays = 30,
  options: RequestOptions = {},
): Promise<{ result: CppExpiringResponse }> {
  return request(`${CPP_BASE}/expiring-soon?withinDays=${withinDays}`, { ...options, method: 'GET' });
}

/** Lists CPP partners (never includes apiKeyId), for partner dropdowns/filters. */
export async function listCppPartners(options: RequestOptions = {}): Promise<{ result: Array<CppPartner> }> {
  return request(`${BASE_PATH}/partners/v1/partners`, { ...options, method: 'GET' });
}
